import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import { UserCheck, LogOut, Settings } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const ImpersonationIndicator = () => {
  const [stopping, setStopping] = useState(false);
  const { impersonation, stopImpersonation } = useAuth();
  const navigate = useNavigate();

  // Don't render if not impersonating
  if (!impersonation.isImpersonating) {
    return null;
  }

  const handleStopImpersonation = async () => {
    try {
      setStopping(true);
      await stopImpersonation();
      
      // Navigate back to admin settings
      navigate('/admin/settings');
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      setStopping(false);
    }
  };

  const handleBackToSettings = () => {
    navigate('/admin/service-ops-users');
  };

  return (
    <Alert 
      severity="info" 
      sx={{ 
        mb: 2,
        borderRadius: 1,
        '& .MuiAlert-message': {
          width: '100%'
        }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        width: '100%'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <UserCheck size={20} style={{ marginRight: '8px' }} />
          <Typography variant="body1">
            <strong>Managing User:</strong> {impersonation.impersonatedUser?.username}
            <br />
            <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
              You are currently managing this user's account. All actions will be performed as this user.
            </Typography>
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Settings size={16} />}
            onClick={handleBackToSettings}
            disabled={stopping}
          >
            Back to User List
          </Button>
          
          <Button
            variant="contained"
            size="small"
            startIcon={stopping ? <CircularProgress size={16} /> : <LogOut size={16} />}
            onClick={handleStopImpersonation}
            disabled={stopping}
            color="secondary"
          >
            {stopping ? 'Stopping...' : 'Stop Managing User'}
          </Button>
        </Box>
      </Box>
    </Alert>
  );
};

export default ImpersonationIndicator; 